import { useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import type { AuthenticateWithRedirectOptions, MissingFieldsData, OAuthStrategy, SSOContext } from './types';

const DEFAULT_CALLBACK_URL = '/sso-callback';

const OAUTH_REDIRECT_TIMEOUT_MS = 15_000;

export const useSSO = (): SSOContext => {
  const clerk = useClerk();
  const { isLoaded: isSignInLoaded, signIn } = useSignIn();
  const { isLoaded: isSignUpLoaded, setActive: clerkSetActive, signUp } = useSignUp();
  const [activeProvider, setActiveProvider] = useState<OAuthStrategy | null>(null);
  const oauthResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isLoaded = !!clerk.loaded && isSignInLoaded && isSignUpLoaded;

  useEffect(() => {
    return () => {
      clearTimeout(oauthResetTimeoutRef.current);
    };
  }, []);

  const signOut = async (): Promise<void> => {
    try {
      await clerk.signOut();
    } catch (error) {
      throw new Error('Failed to sign out');
    }
  };

  const getToken = async (template?: string): Promise<string | null> => {
    if (!clerk.session) {
      return null;
    }
    try {
      return await clerk.session.getToken(template ? { template } : undefined);
    } catch (error) {
      throw new Error('Failed to retrieve authentication token');
    }
  };

  const startOAuthFlow = async (options: AuthenticateWithRedirectOptions): Promise<void> => {
    if (!isLoaded || !signIn || !signUp) {
      throw new Error('SSO provider is not loaded yet');
    }

    setActiveProvider(options.strategy);

    const callbackUrl = `${window.location.origin}${DEFAULT_CALLBACK_URL}`;
    const redirectUrl = options.redirectUrl ?? callbackUrl;
    const redirectUrlComplete = options.redirectUrlComplete ?? callbackUrl;
    const useSignUpFlow = options.mode === 'signUp';

    try {
      if (!options.skipSignOut && clerk.session) {
        await clerk.signOut().catch(() => {
          // user may not be signed in - it's okay to fail silently here
          return null;
        });
      }

      const redirectPromise = useSignUpFlow
        ? signUp.authenticateWithRedirect({
            redirectUrl,
            redirectUrlComplete,
            strategy: options.strategy,
          })
        : signIn.authenticateWithRedirect({
            redirectUrl,
            redirectUrlComplete,
            strategy: options.strategy,
          });

      // Race the redirect against a timeout to prevent indefinite hanging.
      // On success, the page navigates away and this promise is abandoned.
      // eslint-disable-next-line fp/no-let
      let raceTimeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_resolve, reject) => {
        // eslint-disable-next-line fp/no-mutation
        raceTimeoutId = setTimeout(
          () => reject(new Error('OAuth redirect timed out. Please try again.')),
          OAUTH_REDIRECT_TIMEOUT_MS,
        );
      });

      try {
        await Promise.race([redirectPromise, timeoutPromise]);
      } finally {
        clearTimeout(raceTimeoutId);
        redirectPromise.catch(() => undefined);
      }

      // The promise resolved but we may still be on the page — on mobile
      // Safari, the browser can intercept the navigation and show a native
      // OAuth sheet (e.g. Apple Sign-in) as a system overlay without firing
      // any focus/visibility events.  Schedule a fallback reset through the
      // shared ref.  If the page *does* navigate away the effect cleanup
      // cancels this timer; if focus/visibility listeners fire first they
      // override it with a shorter delay.
      clearTimeout(oauthResetTimeoutRef.current);
      // eslint-disable-next-line fp/no-mutation
      oauthResetTimeoutRef.current = setTimeout(() => {
        setActiveProvider(null);
      }, 3_000);
    } catch (error) {
      clearTimeout(oauthResetTimeoutRef.current);
      setActiveProvider(null);
      throw error;
    }
  };

  const destroyClient = async (): Promise<void> => {
    await clerk.client?.destroy();
  };

  /**
   * Complete the OAuth redirect callback and activate the session.
   * Call this on your /sso-callback route to finish the OAuth flow.
   */
  const handleRedirectCallback = async (): Promise<void> => {
    await clerk.handleRedirectCallback({});
  };

  const setActive = async (params: { session: string }): Promise<void> => {
    if (!clerkSetActive) {
      throw new Error('SSO provider is not loaded yet');
    }
    await clerkSetActive(params);
  };

  const updateSignUp = async (data: MissingFieldsData) => {
    if (!signUp) {
      throw new Error('No sign-up in progress');
    }

    return signUp.update({
      firstName: data.firstName,
      lastName: data.lastName,
    });
  };

  /**
   * Transfer OAuth verification from signIn to signUp (for new users).
   * Use when signIn fails with "external_account_not_found".
   */
  const handleTransferToSignUp = async (): Promise<void> => {
    if (!signUp || !signIn) {
      throw new Error('SSO provider is not loaded yet');
    }

    const result = await signUp.create({ transfer: true });
    if (result.status === 'complete' && result.createdSessionId) {
      await clerkSetActive?.({ session: result.createdSessionId });
    }
  };

  /**
   * Transfer OAuth verification from signUp to signIn (for existing users).
   * Use when signUp fails with "external_account_exists".
   */
  const handleTransferToSignIn = async (): Promise<void> => {
    if (!signIn || !signUp) {
      throw new Error('SSO provider is not loaded yet');
    }

    const result = await signIn.create({ transfer: true });
    if (result.status === 'complete' && result.createdSessionId) {
      await clerkSetActive?.({ session: result.createdSessionId });
    }
  };

  return {
    activeProvider,
    destroyClient,
    getToken,
    handleRedirectCallback,
    handleTransferToSignIn,
    handleTransferToSignUp,
    isLoaded,
    isOAuthRedirecting: activeProvider !== null,
    session: clerk.session,
    setActive,
    signIn: signIn ?? null,
    signOut,
    signUp: signUp ?? null,
    startOAuthFlow,
    updateSignUp,
  };
};
